<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { VDataTable } from 'vuetify/components';
import { onMounted, watch, computed, ref } from 'vue';
import DeliverableTableRow from './DeliverableTableRow.vue';
import PhaseTableRow from './PhaseTableRow.vue';
import type {
    DeliverableState,
    DeliverableStatus,
    Project,
} from '@/gql/graphql';
import { sortByDate } from '@/utils/date';
import { useRoute } from 'vue-router';

export interface DeliverableTableItem {
    name: string;
    id: string;
    description?: string;
    deliverables?: DeliverableTableSubItem[];
    startDate?: string;
    endDate: string;
    project: Pick<
        Project,
        'id' | 'masterPortfolioId' | 'parentProjectId' | 'name'
    >;
    linkedProjectId: string | null;
    parentProjectId?: string;
}

export interface DeliverableTableSubItem {
    id: string;
    name: string;
    type: {
        isMilestone?: boolean;
        status: DeliverableStatus;
    };
    state: DeliverableState;
    startDate?: string;
    endDate: string;
    project: Pick<
        Project,
        'id' | 'masterPortfolioId' | 'parentProjectId' | 'name'
    >;
    linkedProjectId: string | null;
}
const { t } = useI18n({
    messages: {
        nl: {
            phases: 'Fases',
            type: '',
        },
        en: {
            phases: 'Phases',
            type: '',
        },
    },
});

const route = useRoute();

const props = withDefaults(
    defineProps<{
        items: (DeliverableTableItem | DeliverableTableSubItem)[];
        itemsPerPage?: number;
        loading?: boolean;
        canUpdate: boolean;
        canEscalate: boolean;
        currentProjectId: string;
    }>(),
    { itemsPerPage: -1 }
);

defineEmits<{
    phaseDeleted: [];
    loading: [value: boolean];
}>();

const headers = computed(() => [
    {
        title: t('phases'),
        key: 'phase',
        width: '120px',
    },
    {
        title: t('deliverables'),
        key: 'deliverables',
        width: '240px',
    },
    {
        title: t('type'),
        key: 'type',
        width: '32px',
    },
    {
        title: t('status'),
        key: 'status',
        width: '160px',
    },
    {
        title: t('start_date'),
        key: 'startDate',
        width: '160px',
    },
    {
        title: t('end_date'),
        key: 'endDate',
        width: '160px',
    },
    {
        title: t('actions'),
        key: 'actions',
        sortable: false,
        width: '64px',
    },
]);

const sortedItems = computed(() =>
    props.items.map((item) => {
        if (isDeliverableTableItem(item) && item.deliverables) {
            return {
                ...item,
                deliverables: sortByDate([...item.deliverables], 'endDate'),
            };
        }

        return item;
    })
);

// Expand all rows by default and store state while browsing project.
const expanded = ref<string[]>([]);
const storageKey = `deliverables_expanded_${route.params.id}`;

// State is set on load and saved to session storage whenever it changes.
onMounted(() => {
    try {
        // Remove previous stored states
        for (let index = 0; index < sessionStorage.length; index++) {
            const element = sessionStorage.key(index);
            if (
                element?.startsWith('deliverables_expanded') &&
                element !== storageKey
            ) {
                sessionStorage.removeItem(element);
            }
        }

        const savedExpanded = sessionStorage.getItem(storageKey);

        if (savedExpanded) {
            expanded.value = JSON.parse(savedExpanded);
        } else if (!props.loading) {
            expanded.value = getExpandableRows();
        }
    } catch {
        expanded.value = getExpandableRows();
    }
});

// For the initial load without state, wait for the items to be loaded and then expand them all.
watch(
    () => props.items,
    (newValue, oldValue) => {
        if (oldValue.length === 0 && newValue.length > 0) {
            const savedExpanded = sessionStorage.getItem(storageKey);
            if (!savedExpanded) {
                expanded.value = getExpandableRows();
            }
        }
    }
);

watch(
    expanded,
    (newValue) => {
        try {
            sessionStorage.setItem(storageKey, JSON.stringify(newValue));
        } catch (err) {
            console.error('Failed to save state to session storage', err);
        }
    },
    { deep: true }
);

function getExpandableRows() {
    return sortedItems.value
        .filter((item) => isDeliverableTableItem(item))
        .map((item) => item.id);
}

function isDeliverableTableItem(input: unknown): input is DeliverableTableItem {
    if (input && typeof input === 'object' && 'deliverables' in input)
        return true;
    return false;
}
</script>

<template>
    <v-data-table
        v-model:expanded="expanded"
        :sort-by="[{ key: 'endDate', order: 'asc' }]"
        :items="sortedItems"
        :headers
        :items-per-page
        :loading
    >
        <!-- Phases or Unphased deliverable: root-->
        <template #item="{ item, internalItem, isExpanded, toggleExpand }">
            <PhaseTableRow
                v-if="isDeliverableTableItem(item)"
                :item
                :is-expanded="isExpanded(internalItem)"
                @toggle-expand="toggleExpand(internalItem)"
                @delete="$emit('phaseDeleted')"
            />

            <!-- Deliverables without a phase are rendered separate -->
            <DeliverableTableRow
                v-else
                :current-project-id
                :item
                :can-update
                :can-escalate
            />
        </template>

        <!-- Deliverables: sub row -->
        <template #expanded-row="{ item }">
            <template v-if="isDeliverableTableItem(item)">
                <DeliverableTableRow
                    v-for="deliverable in item.deliverables"
                    :key="deliverable.id"
                    subitem
                    :current-project-id
                    :item="deliverable"
                    :can-update
                    :can-escalate
                />
            </template>
        </template>
        <template v-if="itemsPerPage === -1" #bottom></template>
    </v-data-table>
</template>
